import { MyCustomTheme } from '@themes/index';
import { NextPage } from 'next';
import { DefaultSeo } from 'next-seo';
import { AppProps } from 'next/app';
import Router from 'next/router';
import nProgress from 'nprogress';
import { QueryClient, QueryClientProvider } from 'react-query';
import { RecoilEnv, RecoilRoot } from 'recoil';

import { ChakraProvider } from '@chakra-ui/provider';
import { createStandaloneToast } from '@chakra-ui/toast';
import { getBasicTokenString } from '@helpers/credentials';
import { Nunito, Nunito_Sans } from 'next/font/google';
import seoConfig from '../next-seo.config';

const { ToastContainer } = createStandaloneToast();

Router.events.on('routeChangeStart', nProgress.start);
Router.events.on('routeChangeError', nProgress.done);
Router.events.on('routeChangeComplete', nProgress.done);

import { initPWAInstall } from '@helpers/installapp';
import { configEnv } from '@libraries/config';
import { AuthProvider } from '@providers/useAuth';
import { PrintStyles } from '@utils/PrintStyles';
import Head from 'next/head';
import { useEffect } from 'react';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      suspense: false,
    },
    mutations: {
      retry: false,
    },
  },
});
RecoilEnv.RECOIL_DUPLICATE_ATOM_KEY_CHECKING_ENABLED = false;

const fontHeading = Nunito_Sans({
  weight: ['400', '500', '600', '700', '800'],
  subsets: ['latin'],
});
const fontBody = Nunito({
  weight: ['400', '500', '600', '700', '800'],
  subsets: ['latin'],
});

interface CustomAppProps extends AppProps {
  Component: NextPage;
}

const App: NextPage<CustomAppProps> = ({ Component, pageProps }) => {
  const token = getBasicTokenString();
  initPWAInstall();

  useEffect(() => {
    if (configEnv.stage !== 'local') {
      if ('serviceWorker' in navigator) {
        window.addEventListener('load', () => {
          navigator.serviceWorker
            .register('/sw.js')
            .then((registration) => {
              console.log(
                'Service Worker registered with scope:',
                registration.scope,
              );
            })
            .catch((error) => {
              console.error('Service Worker registration failed:', error);
            });
        });
      }
    }
  }, []);

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Head>
      <style jsx global>
        {`
          :root {
            --font-heading: ${fontHeading.style.fontFamily};
            --font-body: ${fontBody.style.fontFamily};
          }
        `}
      </style>
      <DefaultSeo {...seoConfig} />
      <PrintStyles />
      <RecoilRoot>
        <ChakraProvider theme={MyCustomTheme}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider token={token} initialProfile={pageProps?.profile}>
              <Component {...pageProps} />
              <ToastContainer />
            </AuthProvider>
          </QueryClientProvider>
        </ChakraProvider>
      </RecoilRoot>
    </>
  );
};

export default App;
